// @flow

import * as React from 'react';
import moment from 'moment';

import { getDateFormat } from '../../utils';
import ToggleButtonGroupAction from './ToggleButtonGroupAction';

import type { CommonActionProps } from './types.actions';
import { DesktopView, MobileView } from '../../config/mediaQuery';
import Dropdown from '../Dropdown';

type Props = {
  onClick: (filters: any) => void,
  filters: any,
  moduleName?: string,
  isRep?: boolean
};

const DATE_FORMAT = getDateFormat('client');

const FILTER_TYPE = 'Date';
export const DATE_OPTIONS = [
  {
    id: 'MTD',
    value: 'MTD',
    text: 'MTD',
  },
  {
    id: 'YTD',
    value: 'YTD',
    text: 'YTD',
  },
  {
    id: 'YR1',
    value: 'YR1',
    text: '1 YR',
  },
  {
    id: 'YR2',
    value: 'YR2',
    text: '2 YR',
  },
];

/**
 * Build the date-range filter presets. Returns a fresh object on each call
 * so `le` (today) and `ge` (start-of-month / start-of-year / N-years-ago)
 * reflect the moment of invocation, not the moment when this module was
 * first imported.
 *
 * Fixes the stale-across-midnight bug (#907): when this lived as a static
 * module-level constant, users who kept a tab open past midnight would
 * see yesterday's date as `le`. Now `moment()` runs at click time.
 *
 * `now` is parameterized for deterministic testing. Production callers
 * should invoke it with no arguments.
 */
export const getDateFilters = (now /*: moment$Moment | void */) => {
  const n = now || moment();
  const today = n.format(DATE_FORMAT);
  return {
    '': null,
    MTD: {
      Date: {
        ge: { value: n.clone().startOf('month').format(DATE_FORMAT), type: 'raw' },
        le: { value: today, type: 'raw' },
      },
    },
    YTD: {
      Date: {
        ge: { value: n.clone().startOf('year').format(DATE_FORMAT), type: 'raw' },
        le: { value: today, type: 'raw' },
      },
    },
    YR1: {
      Date: {
        ge: { value: n.clone().subtract(1, 'years').format(DATE_FORMAT), type: 'raw' },
        le: { value: today, type: 'raw' },
      },
    },
    YR2: {
      Date: {
        ge: { value: n.clone().subtract(2, 'years').format(DATE_FORMAT), type: 'raw' },
        le: { value: today, type: 'raw' },
      },
    },
  };
};

class DateFilterAction extends React.Component<Props & CommonActionProps> {
  static defaultProps = {
    size: 'xsmall',
    disabled: false,
    filterType: FILTER_TYPE,
  };

  constructor(props) {
    super(props);

    this.state = {
      // to match in UI default button selection on initial mount,
      // caused by the GridOData auto select after connection group panel to grid
      selectedFilter: DATE_OPTIONS[0].value,
    };
  }

  handleChange = value => {
    const filter = getDateFilters()[value];

    this.setState({
      selectedFilter: value,
    });

    this.props.onClick(this.props.filterType, filter);
  };

  /**
   * Identify which preset (if any) the current filter matches.
   *
   * Shape-based comparison: checks whether `ge` is the start-of-month,
   * start-of-year, or N-years-prior of `le`. This is stable across midnight,
   * unlike a deepEqual against a freshly-built DATE_FILTERS (which would
   * fail to match a filter saved before midnight because `le` would no
   * longer equal today's formatted date).
   */
  getSelectedFilter = () => {
    const filterValue =
      this.props.filters && this.props.filters[this.props.filterType];
    if (!filterValue) return '';

    const geStr = filterValue.Date && filterValue.Date.ge && filterValue.Date.ge.value;
    const leStr = filterValue.Date && filterValue.Date.le && filterValue.Date.le.value;
    if (!geStr || !leStr) return '';

    const ge = moment(geStr, DATE_FORMAT);
    const le = moment(leStr, DATE_FORMAT);
    if (!ge.isValid() || !le.isValid()) return '';

    if (ge.isSame(le.clone().startOf('month'), 'day')) return 'MTD';
    if (ge.isSame(le.clone().startOf('year'), 'day')) return 'YTD';
    if (ge.isSame(le.clone().subtract(1, 'years'), 'day')) return 'YR1';
    if (ge.isSame(le.clone().subtract(2, 'years'), 'day')) return 'YR2';

    return '';
  };

  render() {
    const { size, disabled } = this.props;

    return (
      <>
        <DesktopView>
          <ToggleButtonGroupAction
            onChange={this.handleChange}
            bsSize={size}
            disabled={disabled}
            type="radio"
            value={this.state.selectedFilter}
            options={DATE_OPTIONS}
            name="dateFilter"
          />
        </DesktopView>
        <MobileView>
          <Dropdown
            id="dateFilter"
            disabled={this.props.disabled}
            onChange={this.handleChange}
            selected={this.state.selectedFilter}
            options={DATE_OPTIONS}
          />
        </MobileView>
      </>
    );
  }
}

export default DateFilterAction;
